import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_CHANNELS,
  CAMPAIGN_STEP_STATES,
  CampaignChannelBadges,
  CampaignMessageEditor,
} from '#/entities/campaign'
import type { CampaignSequenceStep } from '#/entities/campaign'
import { htmlToMultilineText } from '#/shared/lib/html'
import { cn } from '#/shared/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import { MoreHorizontalIcon } from '#/shared/ui/icons'

const EM_DASH = '—'

export interface CampaignStepActions {
  onEdit: () => void
  onToggleActive: (isActive: boolean) => void
  /** Send the step to this 1-based position; the service shuffles the rest. */
  onMove: (position: number) => void
  /** Absent at the ends of the sequence — there is nowhere to go. */
  canMoveUp: boolean
  canMoveDown: boolean
  onDelete: () => void
  /** Steps are removable only while the campaign is a draft. */
  canDelete: boolean
}

interface CampaignStepCardProps {
  step: CampaignSequenceStep
  actions?: CampaignStepActions
  onBodyChange?: (body: string) => void
}

function StatePill({ step }: { step: CampaignSequenceStep }) {
  const sent = step.state === CAMPAIGN_STEP_STATES.sent
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.75 text-xs font-medium',
        sent ? 'bg-green-50 text-campaign-done' : 'bg-gray-100 text-gray-500',
      )}
    >
      {step.stateLabel}
    </span>
  )
}

function StepMenu({
  step,
  actions,
}: {
  step: CampaignSequenceStep
  actions: CampaignStepActions
}) {
  const { t } = useTranslation()
  // Retiring keeps the step's send history attached to the campaign; deleting
  // would take it with the step, which is why the service only allows a delete
  // while nothing has gone out.
  const isActive = step.isActive
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('campaigns.sequence.actions.more', {
            position: step.position,
          })}
          className="flex size-5 cursor-pointer items-center justify-center rounded text-gray-300 hover:bg-gray-100"
        >
          <MoreHorizontalIcon className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onSelect={actions.onEdit}>
          {t('campaigns.sequence.actions.editStep')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => actions.onToggleActive(!isActive)}>
          {t(
            isActive
              ? 'campaigns.sequence.actions.deactivate'
              : 'campaigns.sequence.actions.activate',
          )}
        </DropdownMenuItem>
        {actions.canMoveUp && (
          <DropdownMenuItem onSelect={() => actions.onMove(step.position - 1)}>
            {t('campaigns.sequence.actions.moveUp')}
          </DropdownMenuItem>
        )}
        {actions.canMoveDown && (
          <DropdownMenuItem onSelect={() => actions.onMove(step.position + 1)}>
            {t('campaigns.sequence.actions.moveDown')}
          </DropdownMenuItem>
        )}
        {actions.canDelete && (
          <DropdownMenuItem variant="destructive" onSelect={actions.onDelete}>
            {t('campaigns.sequence.actions.delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function StepHeader({ step, actions }: CampaignStepCardProps) {
  const { t } = useTranslation()
  return (
    <header className="flex flex-wrap items-center justify-between gap-2.5 border-b border-gray-200 px-3.5 py-3">
      <div className="flex min-w-0 flex-wrap items-center gap-2.5">
        <span
          aria-hidden="true"
          className="flex size-5 shrink-0 items-center justify-center rounded-full bg-campaign-running text-2xs font-medium text-white"
        >
          {step.position}
        </span>
        <span className="text-sm font-medium text-gray-900">
          {t('campaigns.detail.step.title', { position: step.position })}
        </span>
        <CampaignChannelBadges channels={[step.channel]} />
        <span className="text-xs text-gray-300">{step.timing}</span>
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        <StatePill step={step} />
        {actions != null && (
          <>
            <button
              type="button"
              onClick={actions.onEdit}
              className="cursor-pointer text-xs font-medium text-campaign-running hover:underline"
            >
              {t('campaigns.sequence.actions.edit')}
            </button>
            <StepMenu step={step} actions={actions} />
          </>
        )}
      </div>
    </header>
  )
}

/**
 * Six figures under the message. The service names each one and flips the
 * first from `sent` to `queued` on a step still ahead, so the key is what gets
 * translated — reading `state` to choose a word would only disagree with it.
 */
function StepFooter({ step }: { step: CampaignSequenceStep }) {
  const { t } = useTranslation()
  return (
    <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-gray-200 bg-gray-50 px-3.5 py-2.5">
      <p className="text-xs text-gray-500">{step.footerNote}</p>
      <ul className="flex flex-wrap items-baseline gap-3.5">
        {step.stats.map((stat) => (
          <li key={stat.key} className="flex items-baseline gap-1.25">
            <span
              className={cn(
                'text-xs font-medium',
                stat.value == null
                  ? 'text-gray-300'
                  : stat.strong
                    ? 'text-announce-ink'
                    : 'text-gray-900',
              )}
            >
              {stat.value ?? EM_DASH}
            </span>
            <span className="text-xs text-gray-300">
              {t(`campaigns.sequence.stats.${stat.key}`)}
            </span>
          </li>
        ))}
      </ul>
    </footer>
  )
}

/**
 * An SMS body is plain text, so it stays editable in place with its merge
 * fields inked. An email body is HTML written in the rich editor — flattened
 * to text for this preview rather than typed raw into a textarea, which is
 * where the markup would otherwise show. Edit opens the real editor.
 *
 * A task step has no body at all — nothing is sent, and the footer already
 * says what the work is and when it is due. Dropping an editable message box
 * on it would invite typing into a field the service does not read.
 */
function StepBody({
  step,
  onBodyChange,
  label,
}: {
  step: CampaignSequenceStep
  onBodyChange?: (body: string) => void
  label: string
}) {
  if (step.channel === CAMPAIGN_CHANNELS.task) return null
  if (step.channel === CAMPAIGN_CHANNELS.email) {
    return (
      <p className="text-sm leading-5.5 whitespace-pre-line text-gray-900">
        {htmlToMultilineText(step.body)}
      </p>
    )
  }
  return (
    <CampaignMessageEditor
      value={step.body}
      onChange={(body) => onBodyChange?.(body)}
      label={label}
      // The editor carries its own padding, so pull it back into line with
      // the subject above it.
      className="-mx-2.5"
    />
  )
}

export function CampaignStepCard({
  step,
  actions,
  onBodyChange,
}: CampaignStepCardProps) {
  const { t } = useTranslation()
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <StepHeader step={step} actions={actions} />
      <div className="flex flex-col gap-2.5 p-3.5">
        {step.subject != null && (
          <p className="flex flex-wrap items-center gap-2.5">
            <span className="text-xs text-gray-300">
              {/* One field, two meanings: a task step's title is stored where
                  an email step keeps its subject line. */}
              {t(
                step.channel === CAMPAIGN_CHANNELS.task
                  ? 'campaigns.sequence.taskTitle'
                  : 'campaigns.sequence.subject',
              )}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {step.subject}
            </span>
          </p>
        )}
        {step.threadNote != null && (
          <div className="flex items-start gap-2 rounded-md bg-gray-50 px-2.5 py-2">
            <span
              aria-hidden="true"
              className="mt-1.5 size-1.5 shrink-0 rounded-full bg-campaign-running"
            />
            <p className="text-xs text-gray-500">{step.threadNote}</p>
          </div>
        )}
        <StepBody
          step={step}
          onBodyChange={onBodyChange}
          label={t('campaigns.sequence.messageLabel', {
            position: step.position,
          })}
        />
      </div>
      <StepFooter step={step} />
    </section>
  )
}
